import React, { useState } from 'react';
import { KeyboardArrowUp } from '@mui/icons-material';
import {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table as MuiTable,
  Collapse,
} from '@mui/material';
import { type HeadCell } from './types';
import { IconButton } from '../IconButton';
import { Checkbox } from '../Checkbox';

type CollapsibleCellProps<T> = {
  index: number;
  showIndex: boolean;
  showCheckBox: boolean;
  checked: boolean;
  row: T;
  fontSize: number;
  refetch?: any;
  isTableRowDraggable: boolean;
  visibleHeadCells: Array<HeadCell<T>>;
  collapsedHeadCells: Array<HeadCell<T>>;
  handleDragStart: (index: number) => () => void;
  handleDragOver: (event: React.DragEvent<HTMLTableRowElement>) => void;
  handleDrop: (index: number) => () => void;
  handleCheck: (index: number) => () => void;
};

export const CollapsibleCell = <T,>({
  index,
  row,
  refetch,
  showIndex,
  showCheckBox,
  checked,
  isTableRowDraggable,
  visibleHeadCells,
  collapsedHeadCells,
  fontSize,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleCheck,
}: CollapsibleCellProps<T>): React.ReactNode => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const collapsedColumns = collapsedHeadCells.length;

  const isCollapsibleTable = collapsedColumns > 0;
  const visibleColumns = visibleHeadCells.length + Number(isCollapsibleTable);

  return (
    <>
      <TableRow
        draggable={isTableRowDraggable}
        onDragStart={handleDragStart(index)}
        onDragOver={handleDragOver}
        onDrop={handleDrop(index)}
        key={index}
        sx={{
          width: '100%',
        }}
      >
        {showIndex && (
          <TableCell
            width={'2%'}
            className="!p-0 !font-bold !text-[#951D1D] !whitespace-nowrap"
          >
            {index + 1}
          </TableCell>
        )}
        {showCheckBox && (
          <TableCell>
            <Checkbox
              size="small"
              value={checked}
              onChange={handleCheck(index)}
              label=""
            />
          </TableCell>
        )}
        {visibleHeadCells.map(headCell => {
          const hoverText = headCell.getHoverText
            ? headCell.getHoverText(row)
            : typeof headCell.render(row) !== 'string'
              ? undefined
              : headCell.render(row)?.toString();

          const minWidth: number = headCell.minWidth ?? 100 / visibleColumns;
          const width = headCell.width ?? `${minWidth}%`;
          return (
            <TableCell
              className="cursor-pointer"
              align="center"
              key={headCell.id as string}
              style={{
                width,
                overflow: 'auto',
                maxHeight: '20em',
                fontSize,
                margin: 0,
                wordBreak: 'break-word',
                ...headCell.customStyle,
              }}
              title={hoverText}
            >
              {headCell.render(row, refetch)}
            </TableCell>
          );
        })}

        {isCollapsibleTable && (
          <TableCell
            style={{
              width: `4%`,
            }}
          >
            <IconButton
              size="small"
              sx={{
                bgcolor: '#951D1D',
                color: 'white',
                ':hover': {
                  bgcolor: '#951D1D',
                  color: 'white',
                },
                transition: 'transform 0.3s ease',
                transform: !collapsed ? 'rotate(-180deg)' : 'rotate(-90deg)', // Rotating the icon when expanded
              }}
              onClick={() => {
                setCollapsed(!collapsed);
              }}
              icon={<KeyboardArrowUp />}
            />
          </TableCell>
        )}
      </TableRow>

      {isCollapsibleTable && (
        <Collapse
          in={!collapsed}
          timeout="auto"
          unmountOnExit
          style={{
            marginLeft: '20px',
            marginRight: '20px',
          }}
        >
          <MuiTable size="small" aria-label="purchases">
            <TableHead>
              <TableRow
                className={!collapsed ? 'expanded-row' : ''}
                sx={{
                  width: '100%',
                  bgcolor: '#B6B6B6 !important',
                  maxheight: '3.5em',
                  ...(!collapsed
                    ? {
                        borderBottom: 'none !important',
                      }
                    : {}),
                }}
              >
                {collapsedHeadCells.map(headCell => {
                  const minWidth: number =
                    headCell.minWidth ?? 100 / collapsedColumns;
                  const width = headCell.width ?? `${minWidth}%`;

                  return (
                    <TableCell
                      align="center"
                      key={headCell.id as string}
                      style={{
                        width,
                        fontSize,
                        ...headCell.customStyle,
                      }}
                    >
                      {headCell.label}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow
                sx={{
                  width: '100%',
                }}
              >
                {collapsedHeadCells.map(headCell => {
                  const hoverText =
                    typeof headCell.render(row) !== 'string'
                      ? undefined
                      : headCell.render(row)?.toString();

                  const minWidth: number =
                    headCell.minWidth ?? 100 / collapsedColumns;
                  const width = headCell.width ?? `${minWidth}%`;

                  return (
                    <TableCell
                      className="cursor-pointer"
                      align="center"
                      key={headCell.id as string}
                      style={{
                        width,
                        overflow: 'auto',
                        maxHeight: '18em',
                        fontSize,
                      }}
                      title={hoverText}
                    >
                      {headCell.render(row)}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </MuiTable>
        </Collapse>
      )}
    </>
  );
};
